// Fondation partagée du cœur métier (Business -> Shop) pour tous les modules
// connectés à l'API (Inventaire, Clients, Ventes, Factures, Dashboard).
//
// Historique: cette logique vivait dans src/hooks/useBusinessContext.js sous
// forme de hook local — chaque module qui l'appelait déclenchait son PROPRE
// bootstrap (getBusiness -> createBusiness? -> getShops -> createShop?) à
// chaque montage, sans aucun partage entre pages. Résultat mesuré: naviguer
// Dashboard -> Ventes -> Boutiques -> Clients relançait ces 2-4 requêtes à
// chaque fois et réaffichait "Préparation de votre entreprise/boutique..."
// à chaque page. Ce Provider exécute le bootstrap UNE SEULE FOIS par session
// (tant que l'utilisateur authentifié ne change pas) et partage le résultat:
// naviguer entre pages ne refait plus jamais cet appel réseau tant que les
// données restent valides pour la session. `reload()` reste disponible pour
// un rafraîchissement explicite (ex: après création manuelle d'une boutique).
//
// Auto-provisionnement volontaire pour la V1: si l'utilisateur authentifié
// n'a pas encore d'entreprise/boutique côté serveur, une entreprise et une
// boutique par défaut sont créées silencieusement (pas de nouvel écran
// d'onboarding requis pour ce parcours minimal). L'utilisateur pourra
// renommer/gérer ces entités plus tard via /api/business et /api/shops.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppContext } from '../App';
import { useAuth } from './AuthContext';
import { getBusiness, createBusiness, getShops, createShop } from '../services/apiClient';

export const BusinessContext = createContext(null);

export function BusinessProvider({ children }) {
  const { user } = useContext(AppContext);
  const { currentUser } = useAuth();
  const [business, setBusiness] = useState(null);
  const [shops, setShops] = useState([]);
  const [activeShopId, setActiveShopIdState] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const activeShopKey = useMemo(
    () => `nsara_active_shop_${currentUser?.uid || 'guest'}`,
    [currentUser?.uid]
  );

  const setActiveShopId = useCallback((shopId) => {
    setActiveShopIdState(shopId);
    try { localStorage.setItem(activeShopKey, shopId || ''); } catch (e) {}
  }, [activeShopKey]);

  const bootstrap = useCallback(async () => {
    // P-AUTH-EMAIL-VERIFY-2 (P0): this provider is mounted at the app root,
    // outside <Routes> — it reacts to `currentUser` on its own regardless
    // of which page is showing, so ProtectedRoute's gate cannot be trusted
    // alone to stop this from firing. An unverified user must not get a
    // business/shop created for them just by having a live session. Uses
    // the same Firebase-native emailVerified signal as everywhere else, so
    // Google/completed Magic Link users (always verified) are unaffected.
    if (!currentUser || !currentUser.emailVerified) {
      if (isMountedRef.current) { setLoading(false); setBusiness(null); setShops([]); setActiveShopIdState(''); }
      return;
    }
    if (isMountedRef.current) { setLoading(true); setError(''); }
    try {
      let currentBusiness = (await getBusiness())?.business;
      if (!currentBusiness) {
        const defaultName = user?.nomEntreprise || user?.nom || 'Mon Entreprise';
        currentBusiness = (await createBusiness({
          name: defaultName,
          email: user?.email || currentUser?.email || '',
          currency: user?.devise?.code || 'XOF'
        }))?.business;
      }
      if (!isMountedRef.current) return;
      setBusiness(currentBusiness);

      let currentShops = (await getShops(currentBusiness.id))?.shops || [];
      if (currentShops.length === 0) {
        const defaultShopName = (Array.isArray(user?.boutiques) && user.boutiques[0]?.nom) || 'Boutique principale';
        const created = (await createShop({ businessId: currentBusiness.id, name: defaultShopName }))?.shop;
        currentShops = created ? [created] : [];
      }
      if (!isMountedRef.current) return;
      setShops(currentShops);

      let savedShopId = '';
      try { savedShopId = localStorage.getItem(activeShopKey) || ''; } catch (e) {}
      const validSaved = currentShops.find((s) => s.id === savedShopId);
      setActiveShopIdState(validSaved ? savedShopId : (currentShops[0]?.id || ''));
    } catch (err) {
      if (isMountedRef.current) setError(err?.message || 'Impossible de préparer votre entreprise/boutique.');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.emailVerified]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const value = useMemo(() => ({
    business,
    businessId: business?.id || '',
    shops,
    activeShopId,
    setActiveShopId,
    loading,
    error,
    reload: bootstrap
  }), [business, shops, activeShopId, setActiveShopId, loading, error, bootstrap]);

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBusinessContextValue() {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusinessContextValue must be used within a BusinessProvider');
  return ctx;
}
